import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import httpService from "../../services/httpService";
import Button from 'react-bootstrap/Button';
import Badge from "react-bootstrap/Badge";
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import { FaCheckCircle, FaSearch, FaMicrophone } from "react-icons/fa";
import LoadingFade from "../LoadingFade";
import "../../styles/ClubsToJoin.scss";

const ListenerPodcasters = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [podcasters, setPodcasters] = useState([]);

    const [search, setSearch] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [sort, setSort] = useState("asc");
    const [followed, setFollowed] = useState("");
    const [pagination, setPagination] = useState({
        total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false,
    });

    const fetchPodcasters = () => {
        setLoading(true);
        httpService.get("/dash/podcasters", {
            params: { pageNumber, pageSize, sort, ...(search && { search }), ...(followed !== "" && { followed }) },
        })
            .then((res) => { setPodcasters(res.data.data); setPagination(res.data.pagination); })
            .catch((err) => setError(err.response?.data?.message || "Error loading podcasters"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchPodcasters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, sort, followed]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNumber(1);
        fetchPodcasters();
    };

    const getInitials = (p) => {
        const f = p.user?.first_name?.[0] ?? "";
        const l = p.user?.last_name?.[0] ?? "";
        return (f + l).toUpperCase() || "?";
    };

    const getFullName = (p) =>
        [p.user?.first_name, p.user?.last_name].filter(Boolean).join(" ") || "Unknown";

    return (
        <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'loaded'}>
            <div>
                {/* ── Toolbar ── */}
                <Row className="mb-4 align-items-center g-2">
                    <Col md={4}>
                        <Form onSubmit={handleSearchSubmit}>
                            <InputGroup size="sm">
                                <Form.Control
                                    type="text"
                                    placeholder="Search authors…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <Button variant="primary" type="submit"><FaSearch /></Button>
                            </InputGroup>
                        </Form>
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                        <Form.Select
                            size="sm"
                            value={sort}
                            onChange={(e) => { setSort(e.target.value); setPageNumber(1); }}
                            style={{ width: '90px' }}
                        >
                            <option value="asc">↑ A–Z</option>
                            <option value="desc">↓ Z–A</option>
                        </Form.Select>
                        <Form.Select
                            size="sm"
                            value={followed}
                            onChange={(e) => { setFollowed(e.target.value); setPageNumber(1); }}
                        >
                            <option value="">All</option>
                            <option value="true">Following</option>
                            <option value="false">Not following</option>
                        </Form.Select>
                    </Col>
                    <Col md={5} className="d-flex justify-content-end">
                        <Form.Select
                            size="sm"
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }}
                            style={{ width: 'auto' }}
                        >
                            <option value="6">6</option>
                            <option value="12">12</option>
                            <option value="30">30</option>
                        </Form.Select>
                    </Col>
                </Row>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* ── Podcaster cards ── */}
                {podcasters.length > 0 ? (
                    <>
                        <Row className="g-3">
                            {podcasters.map((podcaster) => (
                                <Col key={podcaster.id} lg={4} md={6}>
                                    <Card className="podcaster-card h-100">
                                        <Card.Body className="p-4 d-flex flex-column">

                                            {/* Avatar + name */}
                                            <div className="d-flex align-items-center gap-3 mb-1">
                                                {podcaster.user?.avatar_url ? (
                                                    <img
                                                        src={podcaster.user.avatar_url}
                                                        alt={getFullName(podcaster)}
                                                        className="podcaster-card__avatar"
                                                    />
                                                ) : (
                                                    <div className="podcaster-card__initials">
                                                        {getInitials(podcaster)}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="podcaster-card__name">{getFullName(podcaster)}</div>
                                                    <div className="podcaster-card__email">{podcaster.user?.email}</div>
                                                </div>
                                            </div>

                                            {/* Footer */}
                                            <div className="podcaster-card__footer mt-auto">
                                                <div className="podcaster-card__follow-label">
                                                    <FaCheckCircle
                                                        style={{ color: podcaster.is_followed ? '#77dd77' : 'rgba(0,0,0,0.25)' }}
                                                    />
                                                    {podcaster.is_followed ? 'Following' : 'Not following'}
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => navigate(
                                                        `/listener-podcaster/${podcaster.guid}`,
                                                        { state: { is_followed: podcaster.is_followed } }
                                                    )}
                                                >
                                                    View Clubs
                                                </Button>
                                            </div>

                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Pagination */}
                        <div className="ctj-pagination">
                            <span>
                                Showing {(pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, pagination.total)} of {pagination.total} authors
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p - 1)}
                                    disabled={!pagination.hasPreviousPage}
                                >
                                    Previous
                                </Button>
                                <span>Page {pageNumber} of {pagination.totalPages}</span>
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p + 1)}
                                    disabled={!pagination.hasNextPage}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    !loading && (
                        <div className="ctj-empty">
                            <FaMicrophone className="ctj-empty__icon" />
                            <p className="ctj-empty__text">No authors found. Try adjusting your search.</p>
                        </div>
                    )
                )}
            </div>
        </LoadingFade>
    );
};

export default ListenerPodcasters;
